// src/pages/TermsOfServicePage.tsx
import React from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiFileText } from "react-icons/fi";
import { usePageTitle } from "../hooks/usePageTitle";

const TermsOfServicePage: React.FC = () => {
	usePageTitle("利用規約");

	return (
		<div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
			<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
				{/* Header */}
				<div className="mb-8">
					<Link
						to="/"
						className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4"
					>
						<FiArrowLeft className="w-4 h-4 mr-1" />
						トップページに戻る
					</Link>
					<div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
						<div className="flex items-center gap-4 mb-4">
							<div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full">
								<FiFileText className="w-6 h-6 text-white" />
							</div>
							<div>
								<h1 className="text-3xl font-bold text-gray-900 dark:text-white">
									b+ ticket 利用規約
								</h1>
								<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
									最終更新日: 2025年11月21日
								</p>
							</div>
						</div>
					</div>
				</div>

				{/* Terms Content */}
				<div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
					<div className="prose prose-gray dark:prose-invert max-w-none">
						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第1条（総則）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								本規約は、合同会社blackadder（以下「当社」といいます）が提供するイベントチケット販売プラットフォーム「b+
								ticket」（以下「本サービス」といいます）の利用条件を定めるものです。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								本サービスを利用される方（以下「ユーザー」といいます）は、本規約に同意の上、本サービスをご利用いただくものとします。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第2条（定義）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								本規約において、次の各号に掲げる用語の意義は、当該各号に定めるとおりとします。
							</p>
							<ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
								<li>
									「ユーザー」:
									本サービスを利用してイベントチケットを購入・閲覧する個人
								</li>
								<li>
									「主催者」:
									本サービスを利用してイベントを企画・開催し、チケットを販売する個人、アーティスト、法人または団体
								</li>
								<li>
									「イベント」:
									主催者が本サービスを通じて企画・開催する催し物、公演、セミナー等
								</li>
								<li>
									「チケット」:
									イベントへの入場権または参加権を表す電子チケット
								</li>
								<li>「アカウント」: 本サービスを利用するために登録する個人情報</li>
							</ol>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第3条（アカウント登録）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								本サービスを利用するには、当社所定の方法によりアカウント登録を行う必要があります。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								ユーザーは、アカウント登録の際、真実かつ正確な情報を提供するものとし、登録情報に変更が生じた場合は速やかに更新するものとします。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								3.
								ユーザーは、アカウントのパスワード等の認証情報を自己の責任において厳重に管理するものとし、第三者に開示または貸与してはなりません。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								4.
								アカウントの不正利用により損害が発生した場合、当社は一切の責任を負いません。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								5.
								当社は、以下のいずれかに該当する場合、アカウント登録を承認しないことがあります。
							</p>
							<ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4 mt-2">
								<li>登録情報に虚偽、誤記または記載漏れがあった場合</li>
								<li>過去に本規約違反等により登録を取り消されたことがある場合</li>
								<li>反社会的勢力等に該当する、または関係を有すると当社が判断した場合</li>
								<li>その他、当社がユーザーとして不適当と判断した場合</li>
							</ul>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第4条（チケットの購入）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								ユーザーは、本サービスを通じて主催者が販売するイベントチケットを購入することができます。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								チケット購入の申込は、本サービス上で所定の手続きを完了した時点で確定します。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								3.
								ユーザーは、チケット購入前にイベントの内容、日時、会場、料金、注意事項等を十分に確認するものとします。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								4.
								購入が確定したチケットは、本サービスのマイページから確認・表示することができます。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								5.
								チケットの販売数には限りがあり、売り切れの場合は購入できません。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第5条（支払い・決済）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1. チケット代金の支払いは、クレジットカード決済により行われます。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								決済処理は、当社が提携する決済代行会社を通じて行われます。決済情報は安全に暗号化され保護されます。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								3.
								チケット代金には、表示価格に加えて決済手数料等が含まれる場合があります。購入前に最終金額を必ず確認してください。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								4.
								決済が正常に完了しない場合、チケットの購入は確定されません。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第6条（チケットのキャンセル・返金）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								ユーザーの都合によるチケット購入後のキャンセル・返金は、原則として受け付けません。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								以下の場合、ユーザーは主催者に対してチケットの返金を請求することができます。
							</p>
							<ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
								<li>主催者がイベントを中止した場合</li>
								<li>
									イベントの内容が事前の告知と著しく異なる場合（主催者の判断による場合を含む）
								</li>
								<li>天災地変等の不可抗力によりイベントが開催できなくなった場合</li>
							</ul>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3 mb-3">
								3.
								返金が行われる場合、決済手数料等を除いた金額が返金されます。返金方法および返金時期は主催者が定めるものとします。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								4.
								返金に関するトラブルについて、ユーザーは主催者と直接協議し解決するものとし、当社は一切の責任を負いません。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第7条（チケットの利用）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								ユーザーは、購入したチケットに記載された日時・会場においてのみ、イベントに入場・参加することができます。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								イベント会場への入場時には、本サービスのマイページに表示される電子チケットを提示する必要があります。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								3.
								主催者またはイベント会場の判断により、本人確認が求められる場合があります。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								4.
								チケットの紛失、盗難、破損等により入場できない場合でも、当社および主催者は再発行の義務を負いません。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								5.
								イベント会場内での行動については、主催者および会場の指示・ルールに従うものとします。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第8条（禁止事項）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								ユーザーは、以下の行為を行ってはなりません。
							</p>
							<ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
								<li>法令、公序良俗に反する行為</li>
								<li>
									購入したチケットを第三者に転売、譲渡、貸与する行為（主催者が明示的に許可した場合を除く）
								</li>
								<li>
									営利目的で、または反復継続してチケットを購入し転売する行為（いわゆる「ダフ屋行為」）
								</li>
								<li>
									チケットを購入する意思がないにもかかわらず購入手続きを行う行為
								</li>
								<li>虚偽の情報を登録または提供する行為</li>
								<li>他のユーザーのアカウントを不正に使用する行為</li>
								<li>第三者の知的財産権、プライバシー権、その他の権利を侵害する行為</li>
								<li>本サービスのシステムに不正にアクセスする行為</li>
								<li>
									本サービスの運営を妨害する行為、または当社のサーバーに過度な負荷をかける行為
								</li>
								<li>当社、主催者または第三者の信用を毀損する行為</li>
								<li>反社会的勢力への利益供与</li>
								<li>その他、当社が不適切と判断する行為</li>
							</ol>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第9条（知的財産権）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								本サービスおよび本サービス上で提供されるコンテンツ（イベント情報、画像、テキスト等）に関する一切の知的財産権は、当社、主催者または当社にライセンスを許諾している者に帰属します。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								ユーザーは、本サービスの利用にあたり、これらのコンテンツを私的利用の範囲を超えて複製、転載、配布等してはなりません。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								3.
								購入したチケットのスクリーンショット等を第三者に提供する行為は、第8条（禁止事項）に該当します。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第10条（免責事項）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								当社は、本サービスが中断されないこと、エラーが発生しないこと、欠陥が修正されること、ユーザーの特定の目的に適合することを保証するものではありません。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								イベントの内容、開催可否、安全性等については主催者が責任を負うものであり、当社は一切の責任を負いません。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								3.
								ユーザーと主催者間、またはユーザー間のトラブルについて、当社は一切の責任を負いません。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								4.
								システム障害、通信障害、天災地変、その他不可抗力により本サービスが利用できない場合、当社は責任を負いません。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								5.
								ユーザーが本サービスを利用することにより、または利用できないことにより被った損害について、当社は一切の責任を負いません。ただし、当社の故意または重過失による場合はこの限りではありません。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								6.
								当社の責に帰すべき事由によりユーザーに損害が発生した場合でも、当社の賠償責任は、直接かつ通常の損害に限られ、その額は当該ユーザーが当該損害の原因となった取引において当社に支払った金額を上限とします。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第11条（個人情報の取り扱い）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								当社は、ユーザーの個人情報を当社プライバシーポリシーに従って適切に取り扱います。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								チケット購入時に提供された個人情報は、主催者にも提供されます。主催者における個人情報の取り扱いについては、各主催者のプライバシーポリシーに従います。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								3.
								当社は、法令に基づく開示請求があった場合、または緊急の必要性がある場合、ユーザーの同意なく個人情報を第三者に開示することがあります。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第12条（アカウント停止・利用制限）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								当社は、ユーザーが以下のいずれかに該当する場合、事前の通知なくアカウントを停止し、または本サービスの利用を制限することができます。
							</p>
							<ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
								<li>本規約に違反した場合</li>
								<li>登録情報に虚偽があることが判明した場合</li>
								<li>不正なチケット転売行為が確認された場合</li>
								<li>決済が正常に完了しない、または不正な決済が疑われる場合</li>
								<li>12ヶ月以上本サービスの利用がない場合</li>
								<li>当社からの連絡に対し、相当期間内に応答がない場合</li>
								<li>その他、当社がユーザーとして不適当と判断した場合</li>
							</ul>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
								2.
								前項の措置によりユーザーに損害が生じた場合でも、当社は一切の責任を負いません。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第13条（サービスの変更・停止）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1.
								当社は、ユーザーへの事前の通知なく、本サービスの内容を変更、または本サービスの提供を停止することができます。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								当社は、以下のいずれかに該当する場合、ユーザーへの事前の通知なく、本サービスの全部または一部を一時的に中断することができます。
							</p>
							<ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
								<li>本サービスのシステムの保守・点検を行う場合</li>
								<li>コンピューター、通信回線等が事故により停止した場合</li>
								<li>天災地変、戦争、暴動等の不可抗力により本サービスの提供ができなくなった場合</li>
								<li>その他、当社が本サービスの一時的な中断が必要と判断した場合</li>
							</ul>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
								3.
								本条に基づく措置によりユーザーに損害が生じた場合でも、当社は一切の責任を負いません。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第14条（規約の変更）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1. 当社は、必要に応じて本規約を変更することができます。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								2.
								本規約を変更する場合、変更後の規約の効力発生日の14日前までに、本サービス上または当社ウェブサイトに掲載する方法によりユーザーに通知します。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								3.
								前項の通知後、ユーザーが本サービスの利用を継続した場合、変更後の規約に同意したものとみなします。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第15条（準拠法および管轄裁判所）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
								1. 本規約の解釈および適用は、日本法に準拠するものとします。
							</p>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								2.
								本サービスまたは本規約に関連してユーザーと当社との間で紛争が生じた場合、東京地方裁判所を第一審の専属的合意管轄裁判所とします。
							</p>
						</section>

						<section className="mb-8">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
								第16条（協議解決）
							</h2>
							<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
								本規約に定めのない事項または本規約の解釈に疑義が生じた場合、ユーザーと当社は誠実に協議の上解決するものとします。
							</p>
						</section>

						<div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
							<p className="text-sm text-gray-600 dark:text-gray-400 text-center">
								合同会社blackadder
							</p>
							<p className="text-sm text-gray-600 dark:text-gray-400 text-center mt-1">
								制定日: 2025年11月21日
							</p>
						</div>
					</div>
				</div>

				{/* Back to Top Button */}
				<div className="mt-6 text-center">
					<Link
						to="/"
						className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl"
					>
						<FiArrowLeft className="w-4 h-4 mr-2" />
						トップページに戻る
					</Link>
				</div>
			</div>
		</div>
	);
};

export default TermsOfServicePage;
